import React, { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import {
  PRESET_CONFIGS,
  computeWinner,
  createEmptyBoard,
  dropPiece,
  isBoardFull
} from "./engine.js";
import type { Cell, Difficulty, GameConfig, LocalGame } from "./types.js";
import ConfettiSplash from "../../lib/ConfettiSplash.js";

type Mode = "cpu" | "local" | "multiplayer";
type PlayerColor = "red" | "yellow" | "green" | "purple" | "orange" | "teal";

const defaultCustom = { rows: 6, cols: 7, connect: 4 };
const COLOR_OPTIONS: PlayerColor[] = ["red", "yellow", "green", "purple", "orange", "teal"];

const CODE_WORDS = [
  "apple", "beach", "candy", "dance", "eagle", "flame", "grape", "honey",
  "ivory", "jelly", "koala", "lemon", "mango", "noble", "ocean", "pearl",
  "queen", "rain", "sugar", "tiger", "umbra", "vivid", "whale", "xenon",
  "yacht", "zebra", "bloom", "coral", "dawn", "ember", "frost", "glow",
  "haven", "iris", "jade", "kite", "lune", "mist", "nova", "opal",
];

function generateCode(): string {
  const pick = () => CODE_WORDS[Math.floor(Math.random() * CODE_WORDS.length)];
  return `${pick()}-${pick()}-${pick()}`;
}

function toLabel(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export default function StackWarsPage({ hideTitle = false }: { hideTitle?: boolean }) {
  const [mode, setMode] = useState<Mode>("cpu");
  const [difficulty, setDifficulty] = useState<Exclude<Difficulty, "custom"> | "custom">("easy");
  const [customConfig, setCustomConfig] = useState(defaultCustom);
  const [playerOneName, setPlayerOneName] = useState("Player 1");
  const [playerTwoName, setPlayerTwoName] = useState("Player 2");
  const [playerOneColor, setPlayerOneColor] = useState<PlayerColor>("red");
  const [playerTwoColor, setPlayerTwoColor] = useState<PlayerColor>("yellow");
  const [game, setGame] = useState<LocalGame>(() => buildGameFromPreset("easy"));
  const [message, setMessage] = useState("Local play mode: single player or two players on one device.");

  const [mpConnected, setMpConnected] = useState(false);
  const [mpPlayerId, setMpPlayerId] = useState<1 | 2 | null>(null);
  const [roomCode, setRoomCode] = useState("");
  const [mpToken, setMpToken] = useState("");
  const [opponentName, setOpponentName] = useState("");
  const [yourTurn, setYourTurn] = useState(false);
  const [mpError, setMpError] = useState("");

  const [joinCode, setJoinCode] = useState(generateCode);

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingRef = useRef(false);
  const roomCodeRef = useRef(roomCode);
  const mpPlayerIdRef = useRef(mpPlayerId);
  const mpConnectedRef = useRef(mpConnected);

  roomCodeRef.current = roomCode;
  mpPlayerIdRef.current = mpPlayerId;
  mpConnectedRef.current = mpConnected;

  const p1 = playerOneName.trim() || "Player 1";
  const p2 = mode === "cpu" ? "Computer" : playerTwoName.trim() || "Player 2";

  const isCpuTurn = mode === "cpu" && game.turn === 2 && game.winner === 0 && !game.isDraw;

  const myName = useMemo(() => {
    if (mode !== "multiplayer") return null;
    if (mpPlayerId === 1) return playerOneName.trim() || "Player 1";
    if (mpPlayerId === 2) return playerOneName.trim() || "Player 2";
    return null;
  }, [mode, mpPlayerId, playerOneName]);

  const opponentDisplayName = useMemo(() => {
    if (mode !== "multiplayer") return null;
    return opponentName || "Opponent";
  }, [mode, opponentName]);

  async function apiPost(path: string, body: object) {
    try {
      const res = await fetch(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      return await res.json();
    } catch {
      return { type: "error", message: "Network error." };
    }
  }

  function handleServerEvent(event: any) {
    switch (event.type) {
      case "opponent-joined":
        setOpponentName(event.playerName);
        setPlayerTwoColor(event.playerColor);
        setMpConnected(true);
        setMessage(`Opponent ${event.playerName} joined! Press Start / Reset Game to begin!`);
        break;

      case "game-state": {
        setGame(event.game);
        setYourTurn(event.yourTurn);
        break;
      }

      case "opponent-left":
        setMpConnected(false);
        setRoomCode("");
        setOpponentName("");
        setMpPlayerId(null);
        setMpToken("");
        setMessage("Opponent left the game.");
        setGame(buildGameFromPreset("easy"));
        break;

      case "error":
        setMpError(event.message);
        break;
    }
  }

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = true;

    async function poll() {
      if (!pollingRef.current) return;
      const code = roomCodeRef.current;
      const pid = mpPlayerIdRef.current;
      if (!code || !pid) { pollingRef.current = false; return; }

      try {
        const res = await fetch(`/api/poll?code=${encodeURIComponent(code)}&playerId=${pid}`);
        const data = await res.json();
        for (const event of data.events) {
          handleServerEvent(event);
          if (event.type === "opponent-left") {
            pollingRef.current = false;
            return;
          }
        }
      } catch {
        // retry on next tick
      }

      if (pollingRef.current) {
        pollRef.current = setTimeout(poll, 200);
      }
    }

    poll();
  }, []);

  function stopPolling() {
    pollingRef.current = false;
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }

  useEffect(() => {
    return () => stopPolling();
  }, []);

  useEffect(() => {
    if (!isCpuTurn) return;
    const timer = window.setTimeout(() => {
      const col = chooseComputerMove(game.board, game.config.connect);
      if (col !== null) applyMove(col);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [isCpuTurn, game]);

  const status = useMemo(() => {
    if (mode === "multiplayer") {
      if (game.winner === mpPlayerId) return "You win!";
      if (game.winner !== 0) return `${opponentDisplayName} wins!`;
      if (game.isDraw) return "Draw game.";
      return yourTurn ? "Your turn" : `${opponentDisplayName}'s turn`;
    }
    if (game.winner === 1) return `${p1} wins!`;
    if (game.winner === 2) return `${p2} wins!`;
    if (game.isDraw) return "Draw game.";
    return `${game.turn === 1 ? p1 : p2}'s turn`;
  }, [game, p1, p2, mode, mpPlayerId, yourTurn, opponentDisplayName]);

  function buildGame(config: GameConfig): LocalGame {
    return {
      config,
      board: createEmptyBoard(config.rows, config.cols),
      turn: 1,
      winner: 0,
      winningCells: [],
      isDraw: false,
      lastMove: null
    };
  }

  function buildGameFromPreset(preset: Exclude<Difficulty, "custom">): LocalGame {
    return buildGame(PRESET_CONFIGS[preset]);
  }

  function getEffectiveConfig(diff: typeof difficulty): GameConfig {
    if (diff === "custom") {
      const rows = Math.max(4, Math.min(12, Math.floor(customConfig.rows)));
      const cols = Math.max(4, Math.min(12, Math.floor(customConfig.cols)));
      const connect = Math.max(3, Math.min(8, Math.floor(customConfig.connect)));
      return { rows, cols, connect: Math.min(connect, Math.max(rows, cols)), difficulty: "custom" };
    }
    return PRESET_CONFIGS[diff];
  }

  function startNewGame() {
    if (mode === "multiplayer") {
      apiPost("/api/reset-game", { code: roomCode, playerId: mpPlayerId, token: mpToken });
      return;
    }

    if (playerOneColor === playerTwoColor) {
      setMessage("Players must use different colors.");
      return;
    }

    setGame(buildGame(getEffectiveConfig(difficulty)));
    setMessage("New local game started.");
  }

  function applyMove(col: number) {
    setGame((prev) => {
      if (prev.winner !== 0 || prev.isDraw) return prev;
      const dropped = dropPiece(prev.board, col, prev.turn);
      if (!dropped.ok) return prev;

      const win = computeWinner(dropped.board, prev.config.connect);
      return {
        ...prev,
        board: dropped.board,
        winner: win.winner,
        winningCells: win.cells,
        isDraw: win.winner === 0 && isBoardFull(dropped.board),
        turn: prev.turn === 1 ? 2 : 1,
        lastMove: { r: dropped.row, c: col, piece: prev.turn, id: Date.now() }
      };
    });
  }

  function handleDrop(col: number) {
    if (mode === "multiplayer") {
      if (!yourTurn) return;
      apiPost("/api/drop-piece", { code: roomCode, playerId: mpPlayerId, token: mpToken, col });
      return;
    }
    if (isCpuTurn) return;
    applyMove(col);
  }

  function setP1Color(next: PlayerColor) {
    if (next === playerTwoColor) return;
    setPlayerOneColor(next);
  }

  function setP2Color(next: PlayerColor) {
    if (next === playerOneColor) return;
    setPlayerTwoColor(next);
  }

  function getCellClass(cell: Cell, r: number, c: number): string {
    const isWinning = game.winningCells.some((pos) => pos.r === r && pos.c === c);
    const isLastDrop = game.lastMove?.r === r && game.lastMove?.c === c;

    if (cell === 1) {
      return `slot piece-${playerOneColor}${isWinning ? " win-cell" : ""}${isLastDrop ? " drop-anim" : ""}`;
    }
    if (cell === 2) {
      return `slot piece-${playerTwoColor}${isWinning ? " win-cell" : ""}${isLastDrop ? " drop-anim" : ""}`;
    }
    return "slot";
  }

  async function handleCreateRoom() {
    const result = await apiPost("/api/create-room", { playerName: p1, playerColor: playerOneColor });
    if (result.type === "error") { setMpError(result.message); return; }
    roomCodeRef.current = result.code;
    mpPlayerIdRef.current = result.playerId;
    setRoomCode(result.code);
    setMpPlayerId(result.playerId);
    setMpToken(result.token);
    setMessage(`Room created! Share this code with a friend: ${result.code}`);
    startPolling();
  }

  async function handleJoinRoom() {
    if (!joinCode.trim()) { setMpError("Enter a room code."); return; }
    const result = await apiPost("/api/join-room", {
      code: joinCode.trim(),
      playerName: playerOneName.trim() || "Player 2",
      playerColor: playerOneColor,
    });
    if (result.type === "error") { setMpError(result.message); return; }
    roomCodeRef.current = joinCode.trim();
    mpPlayerIdRef.current = result.playerId;
    setMpPlayerId(result.playerId);
    setMpToken(result.token);
    setRoomCode(joinCode.trim());
    setOpponentName(result.opponentName);
    setPlayerOneColor(result.opponentColor);
    setPlayerTwoColor(result.assignedColor);
    setMpConnected(true);
    setMessage(`Connected with ${result.opponentName}. Press Start / Reset Game to begin!`);
    startPolling();
  }

  async function handleLeaveGame() {
    await apiPost("/api/leave", { code: roomCode, playerId: mpPlayerId, token: mpToken });
    stopPolling();
    setMpConnected(false);
    setRoomCode("");
    setOpponentName("");
    setMpPlayerId(null);
    setMpToken("");
    setGame(buildGameFromPreset("easy"));
    setMessage("Left the game.");
  }

  function handleModeChange(next: Mode) {
    if (mode === "multiplayer") {
      if (roomCode && mpToken) {
        apiPost("/api/leave", { code: roomCode, playerId: mpPlayerId, token: mpToken });
      }
      stopPolling();
    }
    setMode(next);
    setGame(buildGameFromPreset("easy"));
    if (next !== "multiplayer") {
      setMessage("Local play mode: single player or two players on one device.");
    }
  }

  return (
    <div className="game-shell">
      <ConfettiSplash
        active={game.winner !== 0}
        winnerName={
          mode === "multiplayer"
            ? game.winner === mpPlayerId ? (myName ?? "You") : (opponentDisplayName ?? "Opponent")
            : game.winner === 1 ? p1 : game.winner === 2 ? p2 : undefined
        }
      />
      {!hideTitle && <h1 className="title">Stack Wars</h1>}

      <section className="control-panel">
        <div className="row two-col">
          <label>
            Mode
            <select value={mode} onChange={(e) => handleModeChange(e.target.value as Mode)}>
              <option value="cpu">Single Player vs Computer</option>
              <option value="local">Two Players Local (Swap Turns)</option>
              <option value="multiplayer">Multiplayer Online</option>
            </select>
          </label>
          <label>
            Level
            <select value={difficulty} onChange={(e) => {
              const next = e.target.value as typeof difficulty;
              setDifficulty(next);
              setGame(buildGame(getEffectiveConfig(next)));
              setMessage("");
            }}>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
              <option value="advanced">Advanced</option>
              <option value="custom">Custom</option>
            </select>
          </label>
        </div>

        <div className="row two-col">
          <label>
            {mode === "multiplayer" ? "Your Name" : "Player 1 Name"}
            <input value={playerOneName} onChange={(e) => setPlayerOneName(e.target.value)} maxLength={20} />
          </label>
          {mode !== "multiplayer" && (
            <label>
              {mode === "cpu" ? "Player 2" : "Player 2 Name"}
              <input
                value={mode === "cpu" ? "Computer" : playerTwoName}
                onChange={(e) => setPlayerTwoName(e.target.value)}
                maxLength={20}
                disabled={mode === "cpu"}
              />
            </label>
          )}
        </div>

        <div className="row two-col">
          <label>
            {mode === "multiplayer" ? "Your Color" : "Player 1 Color"}
            <select value={playerOneColor} onChange={(e) => setP1Color(e.target.value as PlayerColor)}>
              {COLOR_OPTIONS.map((color) => (
                <option key={toLabel(color)} value={toLabel(color)} disabled={mode !== "multiplayer" && color === playerTwoColor}>
                  {toLabel(color)}
                </option>
              ))}
            </select>
          </label>
          {mode !== "multiplayer" && (
            <label>
              {mode === "cpu" ? "Computer Color" : "Player 2 Color"}
              <select value={playerTwoColor} onChange={(e) => setP2Color(e.target.value as PlayerColor)}>
                {COLOR_OPTIONS.map((color) => (
                  <option key={toLabel(color)} value={toLabel(color)} disabled={color === playerOneColor}>
                    {toLabel(color)}
                  </option>
                ))}
              </select>
            </label>
          )}
          {mode === "multiplayer" && !mpConnected && !roomCode && (
            <label>
              Room Code
              <input value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="e.g. apple-blue-rain" maxLength={40} />
            </label>
          )}
        </div>

        {difficulty === "custom" && (
          <div className="row three-col">
            <label>Rows <input type="number" min={4} max={12} value={customConfig.rows} onChange={(e) => setCustomConfig((p) => ({ ...p, rows: Number(e.target.value) }))} /></label>
            <label>Columns <input type="number" min={4} max={12} value={customConfig.cols} onChange={(e) => setCustomConfig((p) => ({ ...p, cols: Number(e.target.value) }))} /></label>
            <label>Connect <input type="number" min={3} max={8} value={customConfig.connect} onChange={(e) => setCustomConfig((p) => ({ ...p, connect: Number(e.target.value) }))} /></label>
          </div>
        )}

        {mode === "multiplayer" && !mpConnected && !roomCode && (
          <div className="mp-actions" style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <button className="mp-btn" onClick={handleCreateRoom}>Create Room</button>
            <button className="mp-btn mp-btn-secondary" onClick={handleJoinRoom}>Join Room</button>
          </div>
        )}

        {mode === "multiplayer" && roomCode && !mpConnected && (
          <div style={{ textAlign: 'center' }}>
            <p className="mp-label">Share this code with a friend:</p>
            <p className="mp-code">{roomCode}</p>
            <p className="mp-waiting">Waiting for opponent to join...</p>
            <button className="mp-cancel" onClick={handleLeaveGame}>Cancel</button>
          </div>
        )}

        {mode === "multiplayer" && mpConnected && (
          <div className="mp-connected-bar">
            <span className="mp-connected-info">Connected with <strong>{opponentName}</strong></span>
          </div>
        )}

        <div className="actions">
          {mode !== "multiplayer" && <button onClick={startNewGame}>Start / Reset Game</button>}
          {mode === "multiplayer" && mpConnected && <button onClick={startNewGame}>Start / Reset Game</button>}
          {mode === "multiplayer" && mpConnected && <button className="mp-leave" onClick={handleLeaveGame}>Leave Game</button>}
        </div>

        {mpError && <p className="subtle mp-error">{mpError}</p>}
        <p className="subtle">{message}</p>
      </section>

      {(mode !== "multiplayer" || mpConnected) && (
        <section className="board-panel">
          <p className="status">{status}</p>
          <div className="toy-frame" style={{ gridTemplateColumns: `repeat(${game.config.cols}, minmax(26px, 58px))` }}>
            {game.board.map((row, r) =>
              row.map((cell, c) => (
                <button
                  key={`${r}-${c}-${game.lastMove?.id ?? 0}`}
                  onClick={() => handleDrop(c)}
                  className={getCellClass(cell, r, c)}
                  style={
                    game.lastMove?.r === r && game.lastMove?.c === c
                      ? ({ "--drop-distance": `${(r + 1) * 64}px` } as CSSProperties)
                      : undefined
                  }
                  title={`Drop in column ${c + 1}`}
                  disabled={
                    mode === "multiplayer"
                      ? game.winner !== 0 || game.isDraw || !yourTurn || !mpConnected
                      : game.winner !== 0 || game.isDraw || isCpuTurn
                  }
                />
              ))
            )}
          </div>
          <div className="how-to-play">
            <p className="how-title">How to Play</p>
            <p>1. Pick mode, colors, and preset, then press Start / Reset Game.</p>
            <p>2. Click any column to drop your checker from the top.</p>
            <p>3. First to connect {game.config.connect} in a row wins.</p>
          </div>
        </section>
      )}
    </div>
  );
}

function chooseComputerMove(board: Cell[][], connect: number): number | null {
  const cols = board[0]?.length ?? 0;
  if (cols === 0) return null;

  const validCols: number[] = [];
  for (let c = 0; c < cols; c += 1) {
    if (board[0][c] === 0) validCols.push(c);
  }
  if (validCols.length === 0) return null;

  for (const c of validCols) {
    const tryWin = dropPiece(board, c, 2);
    if (tryWin.ok && computeWinner(tryWin.board, connect).winner === 2) return c;
  }

  for (const c of validCols) {
    const tryBlock = dropPiece(board, c, 1);
    if (tryBlock.ok && computeWinner(tryBlock.board, connect).winner === 1) return c;
  }

  const center = Math.floor(cols / 2);
  const byCenter = [...validCols].sort((a, b) => Math.abs(a - center) - Math.abs(b - center));
  return byCenter[0] ?? validCols[Math.floor(Math.random() * validCols.length)] ?? null;
}